import _ from 'lodash';
import { createClient } from '@/shared/api';
import STUDY_DETAILS_DRAWER_QUERY from '@/entities/study/api/queries/studyDetailsDrawer';
import type { Diagnosis } from '@/shared/api';
import type { StudyQuery, StudyQueryVariables } from '@/shared/api/generated/graphql';

type PhysicianContact = { phone1: string; nightPhone: string; fax: string };
type Physician = {
  id: string;
  firstName: string;
  lastName: string;
  contact: PhysicianContact;
  email: string;
};

export type StudyDetailsDrawer = {
  id: string;
  friendlyId: string;
  onboarding: { pacer: boolean; icd: boolean };
  info: {
    patient: { dob: string };
    interprettingPhysician: Physician;
    referringPhysician: Physician;
    diagnosis: Diagnosis;
    followOnStudy: { duration: number; studyType: string; reportDueDate: string };
  };
  initDuration: number;
  start: string;
  stop: string;
  studyType: string;
  status: string;
  reportDueDate: string;
  rma: string;
  artifact: { percentage: number };
  studyNote: string;
  timezone: string;
  facility: {
    id: string;
    name: string;
    contact: { country: string };
    notificationEmail: string;
    timezone: string;
  };
  device: { id: string; deviceId: string; model: string; firstStudyDate: string };
  linkedStudies: {
    friendlyId: string;
    initDuration: number;
    studyType: string;
    reportDueDate: string;
    id: string;
  }[];
  ecgDetail: any;
  patientAge: number;
  isAFibDetected: boolean;
  hasAFibCode: boolean;
};

/**
 * Loads the study fields shown in the details drawer. Runs the drawer query and throws the first GraphQL error only when no study came back at all.
 *
 * @param id - Identifier of the study.
 * @returns A promise resolving to the study details.
 */
const fetchStudyDetailsDrawer = async (id: string) => {
  const client = await createClient();
  const result = await client.query<StudyQuery, StudyQueryVariables>({
    query: STUDY_DETAILS_DRAWER_QUERY,
    variables: {
      id,
    },
  });
  const { data, errors } = result;
  const { study } = data;
  if (_.isNil(study) && errors?.length) {
    throw new Error(errors[0].message);
  }
  return study;
};

export default fetchStudyDetailsDrawer;
